/**
 * Zahlenformate deuten.
 *
 * Ab ExcelApi 1.12 sagt Excel die Kategorie eines Zahlenformats direkt:
 * `Range.numberFormatCategories` liefert "Currency", "Date", "Percentage" und
 * so weiter. Das ist der verlässliche Weg, aber 1.12 ist neu genug, dass viele
 * Excel-Versionen ihn nicht kennen.
 *
 * Für diese Versionen gibt es hier einen Ersatzweg über die Formatzeichenfolge
 * selbst. Sie ist gut lesbar, weil Office.js sie unabhängig von der
 * Spracheinstellung in englischer Schreibweise liefert: "m/d/yyyy" statt
 * "TT.MM.JJJJ", "#,##0.00" statt "#.##0,00". Der Ersatzweg ist schwächer als
 * die Kategorie – er erkennt exotische Formate nicht –, aber deutlich besser
 * als gar keine Prüfung. Wo er zum Einsatz kommt, sagen die Rückmeldungen das.
 */

import type { CellInfo } from "./workbook";

/** Formatzeichenfolge von Textbausteinen und Farbangaben befreien. */
function kern(numberFormat: string): string {
  return numberFormat.replace(/"[^"]*"/g, "").replace(/\[[^\]]*\]/g, "");
}

/** Trägt die Zelle überhaupt ein gesetztes Zahlenformat? */
export function hatZahlenformat(cell: CellInfo): boolean {
  if (cell.numberFormatCategory !== null) return cell.numberFormatCategory !== "General";
  const format = cell.numberFormat.trim();
  return format.length > 0 && format !== "General" && format !== "Standard";
}

/** Ist ein Datumsformat gesetzt? */
export function istDatumsformat(cell: CellInfo): boolean {
  if (cell.numberFormatCategory !== null) {
    return cell.numberFormatCategory === "Date" || cell.numberFormatCategory === "Time";
  }
  // Ersatzweg: Jedes Datumsformat enthält einen Jahres- oder Tagesplatzhalter.
  return /(y{2,4}|d{1,4})/i.test(kern(cell.numberFormat));
}

/** Ist ein Währungs- oder Buchhaltungsformat gesetzt? */
export function istWaehrungsformat(cell: CellInfo): boolean {
  if (cell.numberFormatCategory !== null) {
    return cell.numberFormatCategory === "Currency" || cell.numberFormatCategory === "Accounting";
  }
  // Ersatzweg: Ein Währungsformat trägt ein Währungszeichen, entweder direkt
  // oder in eckigen Klammern als Gebietsangabe wie [$€-407].
  return /[€$£¥]|\[\$/.test(cell.numberFormat);
}

/** Ist ein Prozentformat gesetzt? */
export function istProzentformat(cell: CellInfo): boolean {
  if (cell.numberFormatCategory !== null) return cell.numberFormatCategory === "Percentage";
  return kern(cell.numberFormat).includes("%");
}

/** Ist die Zelle ausdrücklich als Text formatiert? */
export function istTextformat(cell: CellInfo): boolean {
  if (cell.numberFormatCategory !== null) return cell.numberFormatCategory === "Text";
  return kern(cell.numberFormat).trim() === "@";
}

/**
 * Enthält die Zelle einen echten Datumswert?
 *
 * Ein Datum ist in Excel eine Zahl – der 1. Januar 1900 ist die 1, jeder
 * weitere Tag zählt eins hoch. Sichtbar wird daraus ein Datum erst durch das
 * Zahlenformat. Beides muss also zusammenkommen: der Werttyp einer Zahl und
 * ein Datumsformat.
 */
export function istDatumswert(cell: CellInfo): boolean {
  const zahl = cell.valueType === "Double" || cell.valueType === "Integer";
  return zahl && istDatumsformat(cell);
}

/**
 * Wurde die Kategorie direkt von Excel gemeldet oder über die
 * Formatzeichenfolge erschlossen?
 *
 * Die Rückmeldungen der Aufgaben nennen den Unterschied, damit niemand einem
 * Ersatzweg mehr Gewicht gibt, als er verdient.
 */
export function kategorieDirektGelesen(cell: CellInfo): boolean {
  return cell.numberFormatCategory !== null;
}
